/**
 * Typen und Hilfen der Seite „Eventstromversorgung" (W2-B1, 16.10.2026).
 *
 * Die Typen folgen `GET …/elektro/eventstrom` (EventstromEndpoints.cs, Eventstromlauf.cs). Gerechnet wird
 * beim Abruf; die Seite schreibt nichts — auch nicht in die Leistungsbilanz.
 */

export const PDF_HINWEIS =
  'Der PDF-Abruf legt nur bei geändertem Stand eine neue Berichtsfassung mit eigener Dokumentnummer an; sonst liefert er die abgelegte Fassung (F11). '
  + 'Die Excel-Mappe legt keine Fassung an.';

export const ANNAHMEN_HINWEIS =
  'Gleichzeitigkeit, Selektivitätsfaktor und Grenze des Zusatzschutzes sind unbestätigte Planungsannahmen (ELT-58). '
  + 'Die Selektivität ist nur über das Nennstromverhältnis vorgeprüft — der Nachweis braucht die Kennlinien der '
  + 'Schutzorgane an den Anschlusspunkten.';

export const BILANZ_HINWEIS =
  'Die Leistungsbilanz des Modells wird hier NICHT verändert. Die Seite hält die Anschlusspunkte der FLB (Kap. 8.8) '
  + 'gegen Modell, Netzberechnung, Leistungsbilanz der Fachplanung und SIMARIS und zeigt, was fehlt.';

export function steckername(stecker: string): string {
  return stecker === 'powerlock' ? 'Powerlock' : 'CEE';
}

/** Status des Servers als Text. */
export function eventStatusText(status: string): string {
  switch (status) {
    case 'fehler': return 'Fehler';
    case 'warnung': return 'Warnung';
    case 'ok': return 'in Ordnung';
    case 'gleich': return 'gleich';
    case 'fehlt_im_modell': return 'fehlt im Modell';
    case 'nur_im_modell': return 'nur im Modell';
    default: return status;
  }
}

export interface EvKennzahlen {
  flbEingelesen: boolean; orte: number; punkteFlb: number; punkteModell: number; anschlussKvaFlb: number;
  bedarfKwFlb: number; eventverteiler: number; verteilerNichtImModell: number; nichtAbgebildetKw: number;
  messstellen: number; messstellenImModell: number; befunde: number; warnungen: number; fehler: number;
  aufAnnahmen: boolean;
}

export interface EvOrt {
  ort: string; punkte: number; summeNennstromA: number; groessterPunktA: number; anschlussKva: number;
  anschlussKw: number; gleichzeitigkeit: number; bedarfKw: number; bemessungsstromA: number; verteiler: string | null;
  zuordnung: string | null; verteilerImModell: boolean; verteilerKw: number | null; verteilerKwHerkunft: string | null;
  zuleitungA: number | null; zuleitungHerkunft: string | null; selektivitaetsverhaeltnis: number | null;
  status: string; befunde: string[];
}

export interface EvPunkt {
  ort: string; anzahl: number; nennstromA: number; stecker: string; zweck: string; anschlussKvaJePunkt: number;
  zusatzschutz: string; messung: string; quelle: string | null;
}

export interface EvLaststufe {
  stecker: string; nennstromA: number; flb: number; modell: number; anschlussKvaJePunkt: number; status: string;
}

export interface EvVerteiler {
  kennzeichen: string; bezeichnung: string | null; imModell: boolean; herkunft: string | null; ober: string | null;
  nshv: string | null; modellKw: number | null; quelleKw: number | null; quelleGleichzeitigkeit: number | null;
  simarisStart: string | null; simarisInA: number | null; simarisAusloeser: string | null; simarisIbA: number | null;
  simarisGleichzeitigkeit: number | null; simarisZuleitung: string | null; schutzorgan: string | null;
  nichtAbgebildetKw: number; status: string; befunde: string[];
}

export interface EvNshv {
  nshv: string; verteiler: number; modellKw: number; quelleKw: number; nichtAbgebildetKw: number;
}

export interface EvModellanschluss {
  typ: string | null; nennstromA: number | null; stecker: string; raumnummer: string | null; raumname: string | null;
  verteiler: string | null; verteilerIstEvent: boolean; kreis: string | null;
}

export interface EvMessstelle {
  nr: number; ort: string; punkt: string; nennstromA: number; anforderung: string; imModell: boolean;
}

export interface EvVorgabe {
  feld: string; verwendung: string | null; wert: string; einheit: string | null;
  bestaetigt: boolean; herkunft: string; normbezug: string | null; rueckfall: boolean;
}

export interface EventstromStand {
  flbQuelle: string | null;
  kennzahlen: EvKennzahlen;
  orte: EvOrt[];
  punkte: EvPunkt[];
  laststufen: EvLaststufe[];
  verteiler: EvVerteiler[];
  nshv: EvNshv[];
  modellanschluesse: EvModellanschluss[];
  messstellen: EvMessstelle[];
  vorgaben: EvVorgabe[];
  befunde: string[];
  stand: string;
}
